/**
 * VendorDashboard - ELEGANT WITH ILLUSTRATIONS
 * 
 * Clean white cards with subtle color tints and decorative illustrations
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useVendorConfig } from '../context/VendorConfigContext';
import { useTheme } from '../context/ThemeContext';
import api from '../api/api';
import { PageHeader, PrimaryButton, CardContainer } from '../components/ui';
import { CURRENCY } from '../config/currency';
import { todayLocalIso, toLocalIsoDate } from '../utils/dateLocal';
import {
  ClipboardDocumentListIcon,
  FireIcon,
  TableCellsIcon,
  ChartBarIcon,
  Cog8ToothIcon,
  CreditCardIcon,
  BellAlertIcon,
  ArrowRightIcon,
  CurrencyRupeeIcon,
} from '@heroicons/react/24/solid';

const getStyles = (theme: any) => `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  .dashboard-container {
    font-family: var(--font-family-body, 'Inter', -apple-system, BlinkMacSystemFont, sans-serif);
    background: ${theme.background || '#F8FAFC'};
    min-height: 100vh;
  }

  /* Main Hero Cards */
  .hero-card {
    background: ${theme.components?.card?.background || 'white'};
    border-radius: 20px;
    border: 1px solid ${theme.components?.card?.border || '#E2E8F0'};
    position: relative;
    overflow: hidden;
    transition: all 0.25s ease;
    cursor: pointer;
  }

  .hero-card:hover {
    border-color: ${theme.gray?.[300] || '#CBD5E1'};
    box-shadow: ${theme.components?.card?.hoverShadow || '0 8px 24px rgba(0, 0, 0, 0.08)'};
    transform: translateY(-2px);
  }

  /* Orders Card - uses theme primary with subtle tint */
  .orders-card {
    background: linear-gradient(135deg, ${theme.components?.card?.hover || '#FFF7ED'} 0%, ${theme.components?.card?.background || '#FFFFFF'} 50%);
  }

  .orders-card .illustration {
    position: absolute;
    right: -20px;
    bottom: -20px;
    width: 180px;
    height: 180px;
    opacity: 0.9;
  }

  .orders-card .accent-badge {
    background: ${theme.button?.warning?.solidBg || theme.warning || 'linear-gradient(135deg, #F97316, #EA580C)'};
    color: ${theme.button?.warning?.solidText || 'white'};
  }

  .orders-card .accent-icon {
    background: ${theme.button?.warning?.bg || 'linear-gradient(135deg, #FFEDD5, #FED7AA)'};
    color: ${theme.button?.warning?.text || '#EA580C'};
  }

  /* Kitchen Card - uses theme accent with subtle tint */
  .kitchen-card {
    background: linear-gradient(135deg, ${theme.components?.card?.hover || '#FAF5FF'} 0%, ${theme.components?.card?.background || '#FFFFFF'} 50%);
  }

  .kitchen-card .illustration {
    position: absolute;
    right: -10px;
    bottom: -10px;
    width: 140px;
    height: 140px;
    opacity: 0.9;
  }

  .kitchen-card .accent-badge {
    background: ${theme.button?.primary?.solidBg || theme.primary || 'linear-gradient(135deg, #8B5CF6, #7C3AED)'};
    color: ${theme.button?.primary?.text || 'white'};
  }

  .kitchen-card .accent-icon {
    background: ${theme.button?.secondary?.transparentBg || theme.button?.secondary?.bg || 'linear-gradient(135deg, #EDE9FE, #DDD6FE)'};
    color: ${theme.primary || '#7C3AED'};
  }

  /* Tables Card - uses theme success with subtle tint */
  .tables-card {
    background: linear-gradient(135deg, ${theme.components?.card?.hover || '#ECFEFF'} 0%, ${theme.components?.card?.background || '#FFFFFF'} 50%);
  }

  .tables-card .illustration {
    position: absolute;
    right: -10px;
    bottom: -10px;
    width: 140px;
    height: 140px;
    opacity: 0.9;
  }

  .tables-card .accent-badge {
    background: ${theme.button?.success?.solidBg || theme.success || 'linear-gradient(135deg, #06B6D4, #0891B2)'};
    color: ${theme.button?.success?.solidText || 'white'};
  }

  .tables-card .accent-icon {
    background: ${theme.button?.success?.bg || 'linear-gradient(135deg, #CFFAFE, #A5F3FC)'};
    color: ${theme.button?.success?.text || theme.success || '#0891B2'};
  }

  /* Feature Cards + Quick Stat Cards — now handled by CardContainer */

  .icon-box {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .section-title {
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${theme.gray?.[500] || '#64748B'};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  @keyframes fade-up {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .animate-fade-up {
    animation: fade-up 0.4s ease-out forwards;
  }

  /* SVG Illustrations */
  .food-illustration {
    color: ${theme.warning || '#FDBA74'};
  }

  .cooking-illustration {
    color: ${theme.primary || '#C4B5FD'};
  }

  .table-illustration {
    color: ${theme.success || '#67E8F9'};
  }

  /* Hide scrollbar on mobile */
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

// SVG Illustrations as components
const FoodIllustration = () => (
  <svg viewBox="0 0 200 200" className="w-full h-full food-illustration">
    {/* Plate */}
    <ellipse cx="100" cy="140" rx="80" ry="25" fill="currentColor" opacity="0.3" />
    <ellipse cx="100" cy="135" rx="70" ry="20" fill="currentColor" opacity="0.5" />
    
    {/* Food dome/cloche */}
    <path d="M40 130 Q40 60 100 50 Q160 60 160 130" fill="currentColor" opacity="0.4" />
    <ellipse cx="100" cy="130" rx="60" ry="15" fill="currentColor" opacity="0.6" />
    
    {/* Steam lines */}
    <path d="M70 45 Q65 30 75 20" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.4" />
    <path d="M100 40 Q95 25 105 15" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.5" />
    <path d="M130 45 Q125 30 135 20" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.4" />
    
    {/* Handle on top */}
    <circle cx="100" cy="50" r="8" fill="currentColor" opacity="0.7" />
  </svg>
);

const CookingIllustration = () => (
  <svg viewBox="0 0 160 160" className="w-full h-full cooking-illustration">
    {/* Pan */}
    <ellipse cx="80" cy="110" rx="55" ry="20" fill="currentColor" opacity="0.4" />
    <ellipse cx="80" cy="100" rx="50" ry="15" fill="currentColor" opacity="0.6" />
    
    {/* Pan handle */}
    <rect x="130" y="95" width="25" height="10" rx="3" fill="currentColor" opacity="0.5" />
    
    {/* Flames */}
    <path d="M45 125 Q40 140 50 150 Q55 140 50 130 Q55 125 45 125" fill="#F97316" opacity="0.6" />
    <path d="M70 128 Q65 145 75 155 Q80 145 75 132 Q78 128 70 128" fill="#F97316" opacity="0.7" />
    <path d="M95 125 Q90 142 100 152 Q105 142 100 130 Q103 125 95 125" fill="#F97316" opacity="0.6" />
    <path d="M115 128 Q110 143 120 150 Q123 140 118 132 Q122 128 115 128" fill="#F97316" opacity="0.5" />
    
    {/* Food in pan */}
    <circle cx="65" cy="95" r="8" fill="currentColor" opacity="0.8" />
    <circle cx="85" cy="92" r="10" fill="currentColor" opacity="0.7" />
    <circle cx="100" cy="97" r="7" fill="currentColor" opacity="0.8" />
    
    {/* Steam */}
    <path d="M60 75 Q55 60 65 50" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.3" />
    <path d="M80 70 Q75 55 85 45" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.4" />
    <path d="M100 75 Q95 60 105 50" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.3" />
  </svg>
);

const TableIllustration = () => (
  <svg viewBox="0 0 160 160" className="w-full h-full table-illustration">
    {/* Table top */}
    <rect x="20" y="70" width="120" height="12" rx="4" fill="currentColor" opacity="0.6" />
    
    {/* Table legs */}
    <rect x="30" y="82" width="8" height="50" rx="2" fill="currentColor" opacity="0.4" />
    <rect x="122" y="82" width="8" height="50" rx="2" fill="currentColor" opacity="0.4" />
    
    {/* Chair 1 */}
    <rect x="5" y="90" width="20" height="5" rx="2" fill="currentColor" opacity="0.5" />
    <rect x="8" y="95" width="4" height="25" rx="1" fill="currentColor" opacity="0.4" />
    <rect x="18" y="95" width="4" height="25" rx="1" fill="currentColor" opacity="0.4" />
    <rect x="5" y="60" width="20" height="30" rx="3" fill="currentColor" opacity="0.3" />
    
    {/* Chair 2 */}
    <rect x="135" y="90" width="20" height="5" rx="2" fill="currentColor" opacity="0.5" />
    <rect x="138" y="95" width="4" height="25" rx="1" fill="currentColor" opacity="0.4" />
    <rect x="148" y="95" width="4" height="25" rx="1" fill="currentColor" opacity="0.4" />
    <rect x="135" y="60" width="20" height="30" rx="3" fill="currentColor" opacity="0.3" />
    
    {/* Plate on table */}
    <ellipse cx="80" cy="65" rx="18" ry="6" fill="white" opacity="0.8" />
    <ellipse cx="80" cy="64" rx="14" ry="4" fill="currentColor" opacity="0.2" />
    
    {/* Glass */}
    <rect x="105" y="52" width="10" height="18" rx="2" fill="white" opacity="0.6" />
    <rect x="106" y="54" width="8" height="14" rx="1" fill="currentColor" opacity="0.3" />
  </svg>
);

const VendorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const { features } = useVendorConfig();
  
  const [stats, setStats] = useState({
    pendingOrders: 0,
    preparingOrders: 0,
    todaysOrders: 0,
    todaysRevenue: 0,
    occupiedTables: 0,
    totalTables: 0,
    menuItems: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user?.id) return;
      try {
        setLoading(true);
        const ordersResponse = await api.get(`/vendors/${user.id}/orders`);
        const allOrders = ordersResponse.data?.content || ordersResponse.data || [];
        
        const pendingOrders = allOrders.filter((o: any) => o.status === 'PENDING').length;
        const preparingOrders = allOrders.filter((o: any) => ['ACCEPTED', 'PREPARING'].includes(o.status)).length;
        
        const today = todayLocalIso();
        const todaysOrders = allOrders.filter((o: any) => 
          toLocalIsoDate(new Date(o.createdAt)) === today
        );
        const todaysRevenue = todaysOrders.reduce((sum: number, o: any) => sum + (o.totalAmount || 0), 0);

        const menuResponse = await api.get(`/vendors/${user.id}/menu`);
        const menuItems = (menuResponse.data || []).reduce((t: number, c: any) => t + (c.items?.length || 0), 0);

        let occupiedTables = 0, totalTables = 0;
        if (features?.tableManagement) {
          try {
            // /vendors/{id}/tables/status was a 404 — no controller has ever
            // mapped it. The bare `catch {}` that used to sit here swallowed the
            // failure, so both counts stayed 0 and the tile rendered "0/—"
            // indefinitely: not failing, just quietly wrong. The real endpoint is
            // VenueTableController#getTablesWithOccupancy, vendor-scoped from the
            // auth principal.
            const tablesResponse = await api.get('/api/tables/occupancy');
            const rows: any[] = Array.isArray(tablesResponse.data) ? tablesResponse.data : [];

            // Count only tables whose whole chain is alive. The endpoint filters
            // deleted TABLES but not deleted sections or floors, so it returned
            // 103 rows where the vendor really has 80 — 20 tables sat on deleted
            // sections and 3 on deleted floors. A table on a deleted floor is not
            // capacity, and this matches what /api/tables/layout-summary reports.
            const live = rows.filter((r) => {
              const table = r?.table;
              const section = table?.section;
              const floor = section?.floor;
              return Boolean(table && section && floor)
                && !table.isDeleted && !section.isDeleted && !floor.isDeleted;
            });

            totalTables = live.length;
            // `isOccupied` is the payload's own flag (>=1 active order on the
            // table). The previous code tested `t.status === 'OCCUPIED'`, a field
            // no table endpoint returns — /api/tables gives status: null on every
            // row — so it could never have matched even against a live URL.
            occupiedTables = live.filter((r) => r.isOccupied).length;
          } catch (err) {
            // Never silent again: a broken tile must be visible to whoever is
            // looking at the console, not averaged into a plausible zero.
            console.error('Failed to fetch table occupancy for dashboard tile:', err);
          }
        }

        setStats({ pendingOrders, preparingOrders, todaysOrders: todaysOrders.length, todaysRevenue, occupiedTables, totalTables, menuItems });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [user?.id, features]);

  const formatCurrency = (amount: number) => `${CURRENCY.symbol}${amount.toLocaleString(CURRENCY.locale)}`;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Quick action items for the grid
  const quickActions = [
    { label: 'Orders', subtitle: 'Manage orders', icon: ClipboardDocumentListIcon, to: '/vendor/orders', color: theme.warning || '#D97706', bg: theme.button?.warning?.bg || '#FEF3C7' },
    { label: 'Floor Layout', subtitle: 'Table management', icon: TableCellsIcon, to: '/vendor/tables', color: theme.primary, bg: theme.button.secondary.bg },
    { label: 'Kitchen', subtitle: 'Live display', icon: FireIcon, to: '/vendor/kitchen', color: theme.accent || '#D4845E', bg: theme.button.secondary.bg },
    { label: 'Reports', subtitle: 'Analytics', icon: ChartBarIcon, to: '/vendor/reports', color: theme.success || '#059669', bg: theme.button?.success?.bg || '#D1FAE5' },
    { label: 'Settings', subtitle: 'Configure', icon: Cog8ToothIcon, to: '/vendor/settings', color: theme.gray?.[600] || '#475569', bg: theme.gray?.[100] || '#F1F5F9' },
  ];

  return (
    <>
      <style>{getStyles(theme)}</style>

      <div className="dashboard-container">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 lg:py-8">

          {/* Greeting header */}
          <div className="animate-fade-up">
            <PageHeader
              title={`${getGreeting()}, ${user?.username}`}
              subtitle="Here's what's happening at your restaurant today."
              data-testid="dashboard-header"
            />
          </div>

          {/* Stats row — 4 cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8 animate-fade-up" style={{ animationDelay: '0.05s' }}>
            {/* Revenue */}
            <CardContainer
              onClick={() => navigate('/vendor/reports')}
              className="cursor-pointer"
              data-testid="stat-revenue"
              sx={{ px: 2, py: 2, transition: 'all 0.2s ease' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: theme.button?.success?.bg || '#D1FAE5' }}>
                  <CurrencyRupeeIcon className="w-5 h-5" style={{ color: theme.success || '#059669' }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: theme.text, fontFamily: 'var(--font-family-heading)' }}>
                    {formatCurrency(stats.todaysRevenue)}
                  </p>
                  <p className="text-xs" style={{ color: theme.gray?.[500] }}>Revenue</p>
                </div>
              </div>
            </CardContainer>

            {/* Orders Today */}
            <CardContainer
              onClick={() => navigate('/vendor/orders')}
              className="cursor-pointer"
              data-testid="stat-orders"
              sx={{ px: 2, py: 2, transition: 'all 0.2s ease' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: theme.button?.warning?.bg || '#FEF3C7' }}>
                  <ClipboardDocumentListIcon className="w-5 h-5" style={{ color: theme.warning || '#D97706' }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: theme.text, fontFamily: 'var(--font-family-heading)' }}>
                    {stats.todaysOrders}
                  </p>
                  <p className="text-xs" style={{ color: theme.gray?.[500] }}>Orders Today</p>
                </div>
              </div>
            </CardContainer>

            {/* Tables Occupied */}
            <CardContainer
              onClick={() => navigate('/vendor/tables')}
              className="cursor-pointer"
              data-testid="stat-tables"
              sx={{ px: 2, py: 2, transition: 'all 0.2s ease' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: theme.button.secondary.bg }}>
                  <TableCellsIcon className="w-5 h-5" style={{ color: theme.primary }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: theme.text, fontFamily: 'var(--font-family-heading)' }}>
                    {stats.occupiedTables}/{stats.totalTables || '\u2014'}
                  </p>
                  <p className="text-xs" style={{ color: theme.gray?.[500] }}>Tables Occupied</p>
                </div>
              </div>
            </CardContainer>

            {/* Active KOTs / Pending */}
            <CardContainer
              onClick={() => navigate('/vendor/kitchen')}
              className="cursor-pointer"
              data-testid="stat-active-kots"
              sx={{ px: 2, py: 2, transition: 'all 0.2s ease' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${theme.accent || '#D4845E'}15` }}>
                  <FireIcon className="w-5 h-5" style={{ color: theme.accent || '#D4845E' }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: theme.text, fontFamily: 'var(--font-family-heading)' }}>
                    {stats.pendingOrders + stats.preparingOrders}
                  </p>
                  <p className="text-xs" style={{ color: theme.gray?.[500] }}>Active KOTs</p>
                </div>
              </div>
            </CardContainer>
          </div>

          {/* Quick Actions grid */}
          <div className="animate-fade-up" style={{ animationDelay: '0.1s' }}>
            <p className="section-title mb-4">Quick Actions</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {quickActions.map(action => (
                <CardContainer
                  key={action.label}
                  onClick={() => navigate(action.to)}
                  className="cursor-pointer"
                  data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                  sx={{
                    p: 2,
                    borderRadius: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': { transform: 'translateY(-1px)' },
                  }}
                >
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: action.bg }}>
                    <action.icon className="w-5 h-5" style={{ color: action.color }} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold" style={{ color: theme.text }}>{action.label}</h3>
                    <p className="text-xs" style={{ color: theme.gray?.[500] }}>{action.subtitle}</p>
                  </div>
                </CardContainer>
              ))}
            </div>
          </div>

          {/* Pending orders alert */}
          {stats.pendingOrders > 0 && (
            <div
              className="mt-6 animate-fade-up"
              style={{ animationDelay: '0.15s' }}
            >
              <CardContainer
                onClick={() => navigate('/vendor/orders?status=pending')}
                disableHover
                className="cursor-pointer"
                data-testid="pending-orders-alert"
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: theme.button?.warning?.bg || '#FEF3C7',
                  border: `1px solid ${theme.warning || '#FDE68A'}40`,
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${theme.warning || '#D97706'}20` }}>
                    <BellAlertIcon className="w-5 h-5" style={{ color: theme.warning || '#D97706' }} />
                  </div>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: theme.text }}>{stats.pendingOrders} orders waiting</p>
                    <p className="text-xs" style={{ color: theme.gray?.[500] }}>Tap to view & accept</p>
                  </div>
                </div>
                <ArrowRightIcon className="w-5 h-5" style={{ color: theme.warning || '#D97706' }} />
              </CardContainer>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default VendorDashboard;
